import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Link } from 'react-router-dom';

const plans = [
  {
    name: 'Starter',
    price: '29',
    period: 'month',
    description: 'Perfect for small businesses',
    features: [
      '1,000 messages/month',
      '1 WhatsApp number',
      'Basic analytics',
      'Email support',
      'Contact management',
    ],
    popular: false,
  },
  {
    name: 'Professional',
    price: '99',
    period: 'month',
    description: 'For growing teams',
    features: [
      '10,000 messages/month',
      '3 WhatsApp numbers',
      'Advanced analytics',
      'Priority support',
      'Automation & chatbots',
      'Team collaboration',
      'API access',
    ],
    popular: true,
  },
  {
    name: 'Enterprise',
    price: '299',
    period: 'month',
    description: 'For large organizations',
    features: [
      'Unlimited messages',
      'Unlimited numbers',
      'Custom analytics',
      'Dedicated account manager',
      'Advanced automation',
      'White-label solution',
      'Custom integrations',
      'SLA guarantee',
    ],
    popular: false,
  },
];

export function Pricing() {
  return (
    <div className="bg-gradient-to-b from-[#E8F9EE] to-white py-12 sm:py-16 lg:py-20 px-4" id="pricing" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the perfect plan for your business. No hidden fees.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <Card
              key={index}
              className={`p-5 sm:p-6 lg:p-8 relative bg-white ${
                plan.popular
                  ? 'border-2 border-[#00D95F] shadow-xl md:scale-105'
                  : 'border border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-[#00D95F] text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-semibold">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="text-center mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">{plan.name}</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">{plan.description}</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">${plan.price}</span>
                  <span className="text-gray-600 text-sm sm:text-base">/{plan.period}</span>
                </div>
              </div>

              <Button
                className={`w-full mb-4 sm:mb-6 ${
                  plan.popular
                    ? 'bg-[#00D95F] hover:bg-[#00C054] text-white'
                    : 'bg-white border border-gray-300 text-gray-900 hover:bg-gray-50'
                }`}
                asChild
              >
                <Link to="/signup">Get Started</Link>
              </Button>

              <div className="space-y-2 sm:space-y-3">
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start gap-2 sm:gap-3">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[#E8F9EE] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#00D95F]" />
                    </div>
                    <span className="text-xs sm:text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-8 sm:mt-12 text-center">
          <p className="text-sm sm:text-base text-gray-600">
            Need a custom plan? <Link to="/contact" className="text-[#00D95F] hover:underline font-medium">Contact our sales team</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
